import { css } from 'styled-components'

import { Color } from 'root-constants'

import { LuvlyFlowColor } from '../../../constants'
import { StyledFlowDiscountOffer as Discount } from '../../../pages/FlowDiscountOffer/FlowDiscountOffer.styles'
import { StyledFlowPauseSubscription as Pause } from '../../../pages/FlowPauseSubscription/FlowPauseSubscription.styles'
import { StyledFlowReasons as Reasons } from '../../../pages/FlowReasons/FlowReasons.styles'
import { StyledFlowTechnicalIssues as TechnicalIssues } from '../../../pages/FlowTechnicalIssues/FlowTechnicalIssues.styles'
import {
  FlowBottomArea,
  FlowContent,
  FlowOptions,
  FlowOutcomeTitle,
  FlowTitle,
} from '../../../styles'
import { FlowButton } from '../../FlowButton'
import { StyledFlowFeedbackPopup as Popup } from '../../FlowFeedbackPopup/FlowFeedbackPopup.styles'
import { StyledFlowOption as Option } from '../../FlowOption/FlowOption.styles'

const smallPriceText = css`
  color: ${Color.GRAY};
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0.5px;
`

export const luvlyTheme = css`
  ${FlowTitle} {
    color: ${Color.BLUE_DARK};
    font-family: 'Open Sans', sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
  }

  ${FlowContent} {
    padding-top: 16px;
  }

  ${FlowOutcomeTitle} {
    margin-top: 16px;
  }

  ${FlowOptions} {
    gap: 16px;
  }

  ${FlowBottomArea} {
    padding: 16px 24px;
    border-top: 1px solid ${Color.GRAY_30};
    background: ${Color.WHITE};
  }

  ${FlowButton} {
    border-radius: 30px;
    font-size: 17px;
    font-weight: 700;
    line-height: 24px;
  }

  ${FlowButton}[data-variant='primary'] {
    background-color: ${Color.LUVLY_PRIMARY};
    color: ${Color.WHITE};
  }

  ${FlowButton}[data-variant='secondary'],
  ${FlowButton}[data-variant='transparent'] {
    background-color: ${Color.GRAY_20};
    color: ${Color.BLUE_DARK};
  }

  ${FlowButton}:disabled {
    opacity: 0.4;
  }

  ${Option.Option} {
    min-height: 80px;
    padding: 12px 16px;
    border: 1px solid ${Color.GRAY_30};
    background-color: ${Color.GRAY_20};
    color: ${Color.BLUE_DARK};
    font-weight: 400;
    line-height: 24px;
  }

  ${Option.Option}[data-selected='true'] {
    border-color: ${Color.LUVLY_PRIMARY};
    background-color: ${LuvlyFlowColor.CORAL_LIGHT};
  }

  ${Option.Checkbox} {
    border-color: ${LuvlyFlowColor.GRAY_BORDER};
  }

  ${Option.Option}[data-selected='true'] ${Option.Checkbox} {
    border-color: ${Color.LUVLY_PRIMARY};
    background-color: ${Color.LUVLY_PRIMARY};
  }

  ${Reasons.Title},
  ${TechnicalIssues.Title} {
    margin-bottom: 16px;
  }

  ${Pause.Description} {
    margin-bottom: 16px;
    color: ${Color.DARK_GRAY};
    line-height: 24px;

    strong {
      color: ${LuvlyFlowColor.CORAL_DARK};
      font-weight: 700;
    }
  }

  ${Pause.Title} {
    margin-bottom: 16px;
    font-size: 17px;
    line-height: 24px;
  }

  ${Popup.Content} {
    max-width: 328px;
    box-shadow: none;
  }

  ${Popup.Headline} {
    gap: 12px;
  }

  ${Popup.Description} {
    color: ${LuvlyFlowColor.GRAY_MUTED};
    line-height: 20px;
  }

  ${Popup.Textarea} {
    border-color: ${LuvlyFlowColor.GRAY_BORDER};
    color: ${Color.BLUE_DARK};
    line-height: 20px;

    &::placeholder {
      color: ${LuvlyFlowColor.GRAY_MUTED};
    }
  }

  ${Discount.Title} {
    margin-bottom: 8px;

    mark {
      display: inline;
      margin: 0;
      padding: 0;
      color: ${LuvlyFlowColor.RED};
      font-style: normal;
      font-weight: inherit;

      &::before {
        display: none;
      }
    }
  }

  ${Discount.Description} {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${Color.GRAY_20};
  }

  ${Discount.Plans} {
    gap: 12px;
  }

  ${Discount.Tag} {
    padding: 8px 12px;
    background-color: ${Color.GRAY_30};
    color: ${Color.GRAY};
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.5px;
  }

  ${Discount.Plans} > div:last-child ${Discount.Tag} {
    background-color: ${LuvlyFlowColor.CORAL_DARK};
    color: ${Color.WHITE};
  }

  ${Discount.CurrentPlanBody} {
    gap: 12px;
    padding: 16px;
    border: 1px solid ${Color.GRAY_30};
    border-top: none;
    background-color: ${Color.GRAY_20};
    box-shadow: none;
  }

  ${Discount.NewPlanBody} {
    padding: 12px 16px;
    border: 1px solid ${LuvlyFlowColor.CORAL_DARK};
    border-top: none;
    background-color: ${Color.WHITE};
    box-shadow: none;
  }

  ${Discount.Check},
  ${Discount.Divider} {
    display: none;
  }

  ${Discount.PlanRow} {
    gap: 12px;
  }

  ${Discount.PlanInfo},
  ${Discount.PricePerDay} {
    gap: 4px;
  }

  ${Discount.PricePerDay} {
    min-width: 0;
  }

  ${Discount.CurrentPlanBody} ${Discount.PricePerDay} {
    align-items: flex-end;
  }

  ${Discount.PlanName},
  ${Discount.DailyPrice} {
    color: ${Color.BLUE_DARK};
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    text-transform: none;
  }

  ${Discount.CurrentPlanBody} ${Discount.PlanName},
  ${Discount.CurrentPlanBody} ${Discount.DailyPrice} {
    color: ${Color.GRAY};
  }

  ${Discount.PlanPrice},
  ${Discount.PerDay} {
    ${smallPriceText};
  }

  ${Discount.OldPrice} {
    color: ${Color.GRAY};
    text-decoration-color: ${LuvlyFlowColor.CORAL_DARK};
  }

  ${Discount.Separator} {
    background-color: ${Color.GRAY_30};
  }

  ${Discount.TodayRow} {
    color: ${Color.BLUE_DARK};
    font-weight: 600;
    line-height: 24px;
  }

  ${Discount.Disclaimer},
  ${Discount.DiscountStart} {
    color: ${Color.GRAY};
    font-size: 11px;
    line-height: 14px;
  }

  ${Discount.NewPlanBody} ${FlowButton} {
    box-shadow: 0 6px 4px rgba(255, 114, 98, 0.16);
  }

  ${Discount.DiscountStart} {
    margin-top: -4px;
  }

  ${Discount.Content} + ${FlowBottomArea} {
    padding-inline: 16px;
  }

  ${Discount.CancelButton}[data-variant='secondary'] {
    background-color: ${Color.GRAY_30};
  }
`
